// UserProfile.jsx – minimal user profile page with password change
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../hooks/useAuth";
import { changeOwnPassword } from "../api/userManagementApi";
import "./UserProfile.css";

// Access level Persian labels
const ACCESS_LEVEL_LABELS = {
  admin: "مدیر کل",
  premium: "ویژه",
  standard: "استاندارد",
  basic: "پایه",
};

const ACCESS_LEVEL_COLORS = {
  admin: "#7c3aed",
  premium: "#d97706",
  standard: "#0284c7",
  basic: "#344e41",
};

// ── Password strength indicator ───────────────────────────────────────────────
const getPasswordStrength = (password) => {
  if (!password) return null;
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (score <= 1) return { label: "ضعیف", color: "#e53e3e", width: "25%" };
  if (score === 2) return { label: "متوسط", color: "#d97706", width: "50%" };
  if (score === 3) return { label: "خوب", color: "#0284c7", width: "75%" };
  return { label: "عالی", color: "#344e41", width: "100%" };
};

const UserProfile = ({ onLogout }) => {
  const navigate = useNavigate();
  const { user } = useAuth(onLogout);

  const displayName =
    user?.full_name?.trim() || user?.username?.trim() || "کاربر";
  const avatarLetter = displayName.charAt(0).toUpperCase();
  const accessLevel = user?.access_level || "basic";
  const accessLabel = ACCESS_LEVEL_LABELS[accessLevel] || accessLevel;
  const accessColor = ACCESS_LEVEL_COLORS[accessLevel] || "#344e41";

  // ── Password change state ─────────────────────────────────────────────────
  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  const passwordStrength = getPasswordStrength(form.newPassword);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setFormError("");
    setFormSuccess("");
  };

  const validate = () => {
    if (!form.currentPassword) {
      setFormError("رمز عبور فعلی را وارد کنید.");
      return false;
    }
    if (form.newPassword.length < 6) {
      setFormError("رمز عبور جدید باید حداقل ۶ کاراکتر باشد.");
      return false;
    }
    if (form.newPassword !== form.confirmPassword) {
      setFormError("رمز عبور جدید و تکرار آن مطابقت ندارند.");
      return false;
    }
    if (form.currentPassword === form.newPassword) {
      setFormError("رمز عبور جدید باید با رمز فعلی متفاوت باشد.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setFormError("");
    setFormSuccess("");

    try {
      const result = await changeOwnPassword(
        form.currentPassword,
        form.newPassword,
      );
      setFormSuccess(result?.detail || "رمز عبور با موفقیت تغییر یافت.");
      setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      setFormError(err.message || "خطا در تغییر رمز عبور. دوباره تلاش کنید.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-layout" dir="rtl">
      <div className="page-header">
        <Header onLogout={onLogout} />
      </div>

      <div className="profile-page">
        {/* ── Profile card ───────────────────────────────────────────── */}
        <section className="profile-card">
          <div className="profile-avatar-wrap">
            <div className="profile-avatar">{avatarLetter}</div>
          </div>
          <div className="profile-info">
            <h1 className="profile-name">{displayName}</h1>
            {user?.username && displayName !== user.username && (
              <p className="profile-username">@{user.username}</p>
            )}
            {user?.email && <p className="profile-email">{user.email}</p>}
            <span
              className="profile-badge"
              style={{
                background: `${accessColor}18`,
                color: accessColor,
                borderColor: `${accessColor}40`,
              }}
            >
              {accessLabel}
            </span>
          </div>
        </section>

        {/* ── Change password ────────────────────────────────────────── */}
        <section className="profile-section">
          <div className="profile-section-header">
            <h2 className="profile-section-title">تغییر رمز عبور</h2>
            <p className="profile-section-subtitle">
              برای امنیت بیشتر، از رمز عبور قوی استفاده کنید.
            </p>
          </div>

          <form
            className="password-form"
            onSubmit={handleSubmit}
            noValidate
            autoComplete="off"
          >
            {/* Current password */}
            <div className="form-field">
              <label className="form-label" htmlFor="currentPassword">
                رمز عبور فعلی
              </label>
              <div className="input-wrap">
                <input
                  id="currentPassword"
                  type={showCurrent ? "text" : "password"}
                  className="form-input"
                  value={form.currentPassword}
                  onChange={handleChange("currentPassword")}
                  placeholder="رمز عبور فعلی را وارد کنید"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="input-eye"
                  onClick={() => setShowCurrent((v) => !v)}
                  tabIndex={-1}
                  aria-label={showCurrent ? "مخفی کردن" : "نمایش"}
                >
                  {showCurrent ? "🙈" : "👁️"}
                </button>
              </div>
            </div>

            {/* New password */}
            <div className="form-field">
              <label className="form-label" htmlFor="newPassword">
                رمز عبور جدید
              </label>
              <div className="input-wrap">
                <input
                  id="newPassword"
                  type={showNew ? "text" : "password"}
                  className="form-input"
                  value={form.newPassword}
                  onChange={handleChange("newPassword")}
                  placeholder="حداقل ۶ کاراکتر"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="input-eye"
                  onClick={() => setShowNew((v) => !v)}
                  tabIndex={-1}
                  aria-label={showNew ? "مخفی کردن" : "نمایش"}
                >
                  {showNew ? "🙈" : "👁️"}
                </button>
              </div>
              {/* Strength meter */}
              {passwordStrength && (
                <div className="strength-meter">
                  <div
                    className="strength-bar"
                    style={{
                      width: passwordStrength.width,
                      background: passwordStrength.color,
                    }}
                  />
                  <span
                    className="strength-label"
                    style={{ color: passwordStrength.color }}
                  >
                    {passwordStrength.label}
                  </span>
                </div>
              )}
            </div>

            {/* Confirm password */}
            <div className="form-field">
              <label className="form-label" htmlFor="confirmPassword">
                تکرار رمز عبور جدید
              </label>
              <div className="input-wrap">
                <input
                  id="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  className="form-input"
                  value={form.confirmPassword}
                  onChange={handleChange("confirmPassword")}
                  placeholder="رمز عبور جدید را تکرار کنید"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="input-eye"
                  onClick={() => setShowConfirm((v) => !v)}
                  tabIndex={-1}
                  aria-label={showConfirm ? "مخفی کردن" : "نمایش"}
                >
                  {showConfirm ? "🙈" : "👁️"}
                </button>
              </div>
            </div>

            {/* Error / success messages */}
            {formError && (
              <div className="form-message error" role="alert">
                {formError}
              </div>
            )}
            {formSuccess && (
              <div className="form-message success" role="status">
                {formSuccess}
              </div>
            )}

            <div className="form-actions">
              <button
                type="submit"
                className="btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="btn-spinner" />
                ) : (
                  "ذخیره تغییرات"
                )}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() =>
                  setForm({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: "",
                  })
                }
                disabled={isSubmitting}
              >
                انصراف
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default UserProfile;
